import React from 'react';
import PropTypes from 'prop-types';
import { EditorView } from 'prosemirror-view';
import { useIntl } from 'react-intl';

import { MediaImage } from '@techchange/filestack';
import { messages } from '../../../../../../_shared/i18n/messages';

import { ToolbarButton } from '../../../Toolbar';
import { insertImage } from '../commands';

const propTypes = {

	/**
	 * The id of the DOM element where the editor will be injected.
	 */
	editorDomId: PropTypes.string.isRequired,

	/**
	 * The prosemirror editor view object.
	 */
	editorView: PropTypes.instanceOf(EditorView).isRequired,

	/**
	 * Filestack credentials object for uploading images.
	 */
	filestack: PropTypes.shape({

		apiKey: PropTypes.string.isRequired,

		security: PropTypes.shape({

			policy: PropTypes.string.isRequired,

			signature: PropTypes.string.isRequired,

		}).isRequired,

	}).isRequired,

	/**
	 * Flag indicating if button behavior should be configured for inline placement.
	 */
	inline: PropTypes.bool,

	/**
	 * The provided toolbar props to adhere to accessibility (a11y)
	 * guidelines for button toolbars.
	 *
	 * See here: https://www.w3.org/WAI/ARIA/apg/patterns/toolbar
	 */
	a11yToolbarProps: PropTypes.shape({

		/**
		 * The tab index value for the button. Only the button with
		 * current focus should have tab index value of 0. All others
		 * should have value of -1.
		 */
		tabIndex: PropTypes.oneOf(['0', '-1']).isRequired,

		/**
		 * Callback function to handle keypress events on the button.
		 */
		onKeyDown: PropTypes.func.isRequired,

	}),

};

const ImageButton = (props) => {

	const {
		editorDomId,
		editorView,
		filestack,
		inline,
		a11yToolbarProps = {},
	} = props;

	const intl = useIntl();

	const { selection } = editorView.state;

	const disabled = selection.from !== selection.to;

	const handleImageUploadChange = (imageSrc) => {

		insertImage(imageSrc)(editorView.state, editorView.dispatch);

	};

	if (!filestack) {

		return (
			<ToolbarButton
				id={`${editorDomId}-toolbar-btn-image`}
				icon="far fa-image"
				title={intl.formatMessage(messages.TC_COMPONENTS_TEXT_EDITOR_IMAGE_BUTTON_TITLE)}
				inline={inline}
				disabled
				{...a11yToolbarProps} />
		);

	}

	return (
		<MediaImage
			filestack={filestack}
			id={`${editorDomId}-toolbar-btn-image`}
			onSuccess={handleImageUploadChange}>
			<ToolbarButton
				id={`${editorDomId}-toolbar-btn-image`}
				disabled={disabled}
				icon="far fa-image"
				title={intl.formatMessage(messages.TC_COMPONENTS_TEXT_EDITOR_IMAGE_BUTTON_TITLE)}
				inline={inline}
				{...a11yToolbarProps} />
		</MediaImage>
	);

};

ImageButton.propTypes = propTypes;

export default ImageButton;
